import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  TextField,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
  Alert,
  CircularProgress,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Tabs,
  Tab,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Save as SaveIcon,
  Assignment as AssignmentIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import NotaService from '../../services/nota.service';
import GradeService from '../../services/grade.service';
import CycleService from '../../services/cycle.service';
import TareaService from '../../services/tarea.service';
import NotasList from './NotasList';
import { useTranslation } from '../../hooks/useTranslation';

const NotasInput = () => {
  const { hasPermission } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [inputMode, setInputMode] = useState('grade'); // 'grade' or 'class'
  const [grades, setGrades] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [selectedGrade, setSelectedGrade] = useState('');
  const [selectedCycle, setSelectedCycle] = useState('');
  const [asignments, setAsignments] = useState([]);
  const [currentStudentIndex, setCurrentStudentIndex] = useState(0);
  const [notas, setNotas] = useState({});
  const [cursos, setCursos] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [expandedTareas, setExpandedTareas] = useState({});
  const [tareas, setTareas] = useState({});
  const [tareaForm, setTareaForm] = useState({});
  const [tareaPunteos, setTareaPunteos] = useState({});
  const loadedTareasRef = useRef(new Set());
  // Class mode: list by class (grade+cycle), then by curso show students
  const [classModeCursos, setClassModeCursos] = useState([]);
  const [selectedCursoForClass, setSelectedCursoForClass] = useState('');
  const [classModeNotasByAsignment, setClassModeNotasByAsignment] = useState({});
  const [classModeLoading, setClassModeLoading] = useState(false);
  const [classModeTareas, setClassModeTareas] = useState({});
  const [classModeExpandedTareas, setClassModeExpandedTareas] = useState({});
  const [classModeTareaPunteos, setClassModeTareaPunteos] = useState({});
  const [classModeTareaForm, setClassModeTareaForm] = useState({});

  useEffect(() => {
    loadInitialData();
  }, []);

  // Sync from URL when list "pen" (input notas) is clicked: switch to input tab and load that student
  useEffect(() => {
    const asignmentIdFromUrl = searchParams.get('asignment_id');
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl !== null && tabFromUrl !== undefined) {
      const tab = parseInt(tabFromUrl, 10);
      if (!isNaN(tab)) setTabValue(tab);
    }
    if (asignmentIdFromUrl) {
      loadAsignmentData(asignmentIdFromUrl);
    }
  }, [searchParams.toString()]);

  useEffect(() => {
    const asignmentIdFromUrl = searchParams.get('asignment_id');
    if (asignmentIdFromUrl) return; // handled by URL sync effect
    if (selectedGrade && selectedCycle && inputMode === 'grade') {
      loadStudents();
    } else if (selectedGrade && selectedCycle && inputMode === 'class') {
      loadStudents();
    }
  }, [selectedGrade, selectedCycle, inputMode]);

  // Class mode: load cursos for the selected grade (from first student's cursos)
  useEffect(() => {
    if (inputMode !== 'class' || !selectedGrade || !asignments.length) return;
    const loadCursosForClass = async () => {
      try {
        const firstStudentId = asignments[0].student_id;
        const response = await NotaService.getCursosForStudent(firstStudentId, selectedGrade);
        const data = response.data?.data || [];
        setClassModeCursos(data);
        if (data.length > 0) {
          setSelectedCursoForClass(data[0].id);
        } else {
          setSelectedCursoForClass('');
        }
      } catch (error) {
        console.error('Error loading cursos for class:', error);
        showSnackbar(t('notas.errorLoadingCursos'), 'error');
      }
    };
    loadCursosForClass();
  }, [inputMode, selectedGrade, asignments]);

  // Class mode: load notas per student for the selected curso
  useEffect(() => {
    if (inputMode !== 'class' || !selectedCursoForClass || asignments.length === 0) return;
    const loadClassModeNotas = async () => {
      setClassModeLoading(true);
      try {
        const byAsignment = {};
        await Promise.all(
          asignments.map(async (asignment) => {
            const response = await NotaService.getNotasByAsignment(asignment.id);
            const existingNotas = response.data?.data || [];
            const nota = existingNotas.find(
              (n) => n.curso_id === parseInt(selectedCursoForClass, 10)
            );
            if (nota) {
              byAsignment[asignment.id] = {
                id: nota.id,
                curso_id: nota.curso_id,
                nota1: nota.nota1 ?? '',
                nota2: nota.nota2 ?? '',
                nota3: nota.nota3 ?? '',
                nota4: nota.nota4 ?? '',
                detalle: nota.detalle || false,
              };
            } else {
              byAsignment[asignment.id] = {
                curso_id: parseInt(selectedCursoForClass, 10),
                nota1: '',
                nota2: '',
                nota3: '',
                nota4: '',
                detalle: false,
              };
            }
          })
        );
        setClassModeNotasByAsignment(byAsignment);
      } catch (error) {
        console.error('Error loading class mode notas:', error);
        showSnackbar(t('notas.errorLoadingCursos'), 'error');
      } finally {
        setClassModeLoading(false);
      }
    };
    loadClassModeNotas();
  }, [inputMode, selectedCursoForClass, asignments]);

  // Clear tareas expansion and loaded tareas when switching class/curso so detail doesn't persist
  useEffect(() => {
    if (inputMode !== 'class') return;
    setClassModeExpandedTareas({});
    setClassModeTareas({});
    setClassModeTareaPunteos({});
    setClassModeTareaForm({});
  }, [inputMode, selectedCursoForClass, selectedGrade, selectedCycle]);

  const loadAsignmentData = async (asignmentId) => {
    setLoading(true);
    try {
      const response = await NotaService.getNotasByAsignment(asignmentId);
      const notasData = response.data?.data || [];
      
      if (notasData.length > 0) {
        const firstNota = notasData[0];
        const asignment = firstNota.asignment;
        
        setSelectedGrade(asignment.grade_id);
        setSelectedCycle(asignment.cycle_id);
        
        // Load cursos and set notas
        const cursosRes = await NotaService.getCursosForStudent(asignment.student_id, asignment.grade_id);
        const cursosData = cursosRes.data?.data || [];
        setCursos(cursosData);
        
        const notasMap = {};
        cursosData.forEach(curso => {
          const existingNota = notasData.find(n => n.curso_id === curso.id);
          if (existingNota) {
            notasMap[curso.id] = {
              id: existingNota.id,
              curso_id: curso.id,
              nota1: existingNota.nota1 || '',
              nota2: existingNota.nota2 || '',
              nota3: existingNota.nota3 || '',
              nota4: existingNota.nota4 || '',
              detalle: existingNota.detalle || false,
            };
          } else {
            notasMap[curso.id] = {
              curso_id: curso.id,
              nota1: '',
              nota2: '',
              nota3: '',
              nota4: '',
              detalle: false,
            };
          }
        });
        setNotas(notasMap);
        
        // Set asignments to just this one
        setAsignments([asignment]);
        setCurrentStudentIndex(0);
        setTabValue(0); // Switch to Input tab
      }
    } catch (error) {
      console.error('Error loading asignment data:', error);
      showSnackbar(t('notas.errorLoadingStudentData'), 'error');
    } finally {
      setLoading(false);
    }
  };

  // Depend on length + index so we don't re-run when only ausencias/reportes change (avoids page refresh on type)
  useEffect(() => {
    if (asignments.length > 0 && currentStudentIndex < asignments.length && selectedGrade) {
      const currentAsignment = asignments[currentStudentIndex];
      loadCursosForStudent(currentAsignment.student_id);
    }
  }, [asignments.length, currentStudentIndex, selectedGrade]);

  useEffect(() => {
    if (cursos.length > 0 && asignments.length > 0 && currentStudentIndex < asignments.length) {
      // Clear pending punteos when switching students to prevent cross-contamination
      setTareaPunteos({});
      // Clear loaded tareas tracking when switching students
      loadedTareasRef.current.clear();
      loadNotasForCurrentStudent();
    }
  }, [cursos.length, asignments.length, currentStudentIndex]);

  // Auto-load tareas when detalle is true for any curso
  useEffect(() => {
    if (notas && Object.keys(notas).length > 0 && selectedGrade && selectedCycle) {
      const loadTareasForDetalle = async () => {
        for (const cursoId in notas) {
          const nota = notas[cursoId];
          if (nota?.detalle && nota?.id) {
            // Load tareas for all nota types if not already loaded
            await Promise.all(
              ['nota1', 'nota2', 'nota3', 'nota4'].map(async (notaType) => {
                const key = `${cursoId}_${notaType}`;
                
                // Skip if already loaded or currently loading
                if (loadedTareasRef.current.has(key)) {
                  return;
                }
                
                loadedTareasRef.current.add(key);
                
                try {
                  const response = await TareaService.getByNota(nota.id, notaType);
                  setTareas(prev => ({
                    ...prev,
                    [key]: response.data?.data || [],
                  }));
                } catch (error) {
                  console.error(`Error loading tareas for ${cursoId} ${notaType}:`, error);
                  loadedTareasRef.current.delete(key); // Remove on error so we can retry
                }
              })
            );
          }
        }
      };
      loadTareasForDetalle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notas, selectedGrade, selectedCycle]);

  const loadInitialData = async () => {
    try {
      const [gradesRes, cyclesRes] = await Promise.all([
        GradeService.getAll(),
        CycleService.getAll(),
      ]);
      const gradesData = gradesRes.data?.data || [];
      const cyclesData = cyclesRes.data?.data || [];
      setGrades(gradesData);
      setCycles(cyclesData);
      // Auto-select first cycle when none selected
      if (cyclesData.length > 0) {
        setSelectedCycle(cyclesData[0].id);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      showSnackbar(t('notas.errorLoadingCursos'), 'error');
    }
  };

  const loadStudents = async () => {
    setLoading(true);
    try {
      const response = await NotaService.getStudentsByGradeCycle(selectedGrade, selectedCycle);
      const asignmentsData = response.data?.data || [];
      setAsignments(asignmentsData);
      setCurrentStudentIndex(0);
      
      if (asignmentsData.length > 0) {
        // Load cursos for the first student
        await loadCursosForStudent(asignmentsData[0].student_id);
      }
    } catch (error) {
      console.error('Error loading students:', error);
      showSnackbar(t('notas.errorLoadingStudents'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadCursosForStudent = async (studentId) => {
    try {
      const response = await NotaService.getCursosForStudent(studentId, selectedGrade);
      const cursosData = response.data?.data || [];
      setCursos(cursosData);
      return cursosData;
    } catch (error) {
      console.error('Error loading cursos:', error);
      showSnackbar(t('notas.errorLoadingCursos'), 'error');
      return [];
    }
  };

  const loadNotasForCurrentStudent = async () => {
    if (asignments.length === 0 || currentStudentIndex >= asignments.length || cursos.length === 0) return;

    const currentAsignment = asignments[currentStudentIndex];
    setLoading(true);
    try {
      const response = await NotaService.getNotasByAsignment(currentAsignment.id);
      const existingNotas = response.data?.data || [];
      
      // Initialize notas object with existing data or empty for each curso
      const notasMap = {};
      cursos.forEach(curso => {
        const existingNota = existingNotas.find(n => n.curso_id === curso.id);
        if (existingNota) {
          notasMap[curso.id] = {
            id: existingNota.id,
            curso_id: curso.id,
            nota1: existingNota.nota1 || '',
            nota2: existingNota.nota2 || '',
            nota3: existingNota.nota3 || '',
            nota4: existingNota.nota4 || '',
            detalle: existingNota.detalle || false,
          };
        } else {
          notasMap[curso.id] = {
            curso_id: curso.id,
            nota1: '',
            nota2: '',
            nota3: '',
            nota4: '',
            detalle: false,
          };
        }
      });
      setNotas(notasMap);
    } catch (error) {
      console.error('Error loading notas:', error);
      // Initialize empty notas if none exist
      const notasMap = {};
      cursos.forEach(curso => {
        notasMap[curso.id] = {
          curso_id: curso.id,
          nota1: '',
          nota2: '',
          nota3: '',
          nota4: '',
          detalle: false,
        };
      });
      setNotas(notasMap);
    } finally {
      setLoading(false);
    }
  };

  const calculateTareasSum = (cursoId, notaType) => {
    const tareaKey = `${cursoId}_${notaType}`;
    const cursoTareas = tareas[tareaKey] || [];
    
    let sum = 0;
    cursoTareas.forEach(tarea => {
      // Check if there's a pending punteo in local state
      const pendingKey = `${cursoId}_${tarea.id}`;
      const pendingPunteo = tareaPunteos[pendingKey];
      
      if (pendingPunteo !== undefined && pendingPunteo !== '' && pendingPunteo !== null) {
        // Use pending punteo if available
        const punteoValue = parseFloat(pendingPunteo);
        if (!isNaN(punteoValue)) {
          sum += punteoValue;
        }
      } else if (tarea.punteo !== null && tarea.punteo !== undefined) {
        // Use saved punteo
        const punteoValue = parseFloat(tarea.punteo);
        if (!isNaN(punteoValue)) {
          sum += punteoValue;
        }
      }
    });
    
    return sum > 0 ? Math.round(sum) : null;
  };

  const handleNotaChange = async (cursoId, field, value) => {
    // Handle detalle toggle
    if (field === 'detalle') {
      const newDetalleValue = value;
      const currentAsignment = asignments[currentStudentIndex];
      
      if (!currentAsignment) return;
      
      // Update local state immediately
      setNotas(prev => ({
        ...prev,
        [cursoId]: {
          ...prev[cursoId],
          detalle: newDetalleValue,
        },
      }));
      
      // Save immediately when detalle is toggled to apply to all students
      if (notas[cursoId]?.id) {
        try {
          // Build notas array with updated detalle value
          const notasArray = Object.values(notas)
            .filter(n => n.curso_id)
            .map(n => ({
              curso_id: n.curso_id,
              nota1: n.nota1 === '' || n.nota1 === null ? null : parseInt(n.nota1),
              nota2: n.nota2 === '' || n.nota2 === null ? null : parseInt(n.nota2),
              nota3: n.nota3 === '' || n.nota3 === null ? null : parseInt(n.nota3),
              nota4: n.nota4 === '' || n.nota4 === null ? null : parseInt(n.nota4),
              detalle: n.curso_id === parseInt(cursoId) ? newDetalleValue : (n.detalle || false),
            }));
          
          await NotaService.create({
            asignment_id: currentAsignment.id,
            notas: notasArray,
          });
          
          // Reload notas to get updated values (including detalle for all students)
          await loadNotasForCurrentStudent();
          
          // Auto-load tareas for all nota types if detalle is true
          if (newDetalleValue && selectedGrade && selectedCycle) {
            try {
              await Promise.all(
                ['nota1', 'nota2', 'nota3', 'nota4'].map(async (notaType) => {
                  const key = `${cursoId}_${notaType}`;
                  if (!loadedTareasRef.current.has(key)) {
                    loadedTareasRef.current.add(key);
                    const response = await TareaService.getByNota(notas[cursoId].id, notaType);
                    setTareas(prev => ({
                      ...prev,
                      [key]: response.data?.data || [],
                    }));
                  }
                })
              );
            } catch (error) {
              console.error('Error loading tareas for detalle:', error);
            }
          }
        } catch (error) {
          console.error('Error saving detalle:', error);
          showSnackbar(t('notas.errorSavingNotas'), 'error');
          // Revert the change on error
          setNotas(prev => ({
            ...prev,
            [cursoId]: {
              ...prev[cursoId],
              detalle: !newDetalleValue,
            },
          }));
        }
      }
      return;
    }
    
    // Don't allow changes if detalle is true
    if (notas[cursoId]?.detalle && ['nota1', 'nota2', 'nota3', 'nota4'].includes(field)) {
      return;
    }
    setNotas(prev => ({
      ...prev,
      [cursoId]: {
        ...prev[cursoId],
        [field]: value === '' ? null : parseInt(value) || value,
      },
    }));
  };

  const toggleTareasExpansion = async (cursoId, notaType) => {
    const key = `${cursoId}_${notaType}`;
    const isExpanded = expandedTareas[key];
    setExpandedTareas(prev => ({
      ...prev,
      [key]: !isExpanded,
    }));

    // Load tareas when expanding
    if (!isExpanded && notas[cursoId]?.id && selectedGrade && selectedCycle) {
      try {
        const response = await TareaService.getByNota(notas[cursoId].id, notaType);
        setTareas(prev => ({
          ...prev,
          [key]: response.data?.data || [],
        }));
      } catch (error) {
        console.error('Error loading tareas:', error);
        showSnackbar(t('notas.errorLoadingTareas'), 'error');
      }
    }
  };

  const handleTareaFormChange = (key, field, value) => {
    setTareaForm(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value,
      },
    }));
  };

  const handleCreateTarea = async (cursoId, notaType) => {
    if (!selectedGrade || !selectedCycle) {
      showSnackbar(t('notas.pleaseSelectGradeCycle'), 'error');
      return;
    }

    const key = `${cursoId}_${notaType}`;
    const formData = tareaForm[key] || {};
    if (!formData.name || !formData.total === undefined) {
      showSnackbar(t('notas.pleaseFillAllFields'), 'error');
      return;
    }

    try {
      await TareaService.create({
        grade_id: selectedGrade,
        cycle_id: selectedCycle,
        curso_id: cursoId,
        name: formData.name,
        nota: notaType,
        total: parseFloat(formData.total),
      });

      // Reload tareas for this nota type
      if (notas[cursoId]?.id) {
        const response = await TareaService.getByNota(notas[cursoId].id, notaType);
        setTareas(prev => ({
          ...prev,
          [key]: response.data?.data || [],
        }));
      }

      // Reload notas to get updated values
      await loadNotasForCurrentStudent();

      // Clear form
      setTareaForm(prev => {
        const newForm = { ...prev };
        delete newForm[key];
        return newForm;
      });

      showSnackbar(t('notas.tareaCreatedSuccess'));
    } catch (error) {
      console.error('Error creating tarea:', error);
      showSnackbar(t('notas.errorCreatingTarea'), 'error');
    }
  };

  const handleTareaPunteoChange = (cursoId, tareaId, punteo) => {
    // Store the value locally for immediate UI update
    const key = `${cursoId}_${tareaId}`;
    setTareaPunteos(prev => ({
      ...prev,
      [key]: punteo
    }));
  };

  const handleTareaPunteoBlur = async (cursoId, tareaId, notaType) => {
    if (!notas[cursoId]?.id) {
      showSnackbar(t('notas.pleaseSaveNotaFirst'), 'error');
      // Reset to original value
      const key = `${cursoId}_${tareaId}`;
      setTareaPunteos(prev => {
        const newState = { ...prev };
        delete newState[key];
        return newState;
      });
      return;
    }

    const key = `${cursoId}_${tareaId}`;
    const punteo = tareaPunteos[key];
    const punteoValue = punteo === '' || punteo === null || punteo === undefined 
      ? null 
      : parseFloat(punteo);

    // Validate punteo is not NaN
    if (punteoValue !== null && isNaN(punteoValue)) {
      showSnackbar(t('notas.errorUpdatingScore'), 'error');
      // Reset to original value
      setTareaPunteos(prev => {
        const newState = { ...prev };
        delete newState[key];
        return newState;
      });
      return;
    }

    try {
      await TareaService.updateStudentScore(tareaId, notas[cursoId].id, punteoValue);

      // Small delay to ensure backend has finished recalculating
      await new Promise(resolve => setTimeout(resolve, 300));

      // Reload tareas for this specific nota type to get updated punteo
      if (notaType) {
        const tareaKey = `${cursoId}_${notaType}`;
        try {
          const response = await TareaService.getByNota(notas[cursoId].id, notaType);
          setTareas(prev => ({
            ...prev,
            [tareaKey]: response.data?.data || [],
          }));
        } catch (error) {
          console.error('Error reloading tareas:', error);
        }
      }

      // Reload notas to get updated values (including recalculated nota1, nota2, etc.)
      await loadNotasForCurrentStudent();

      // Clear the local state after successful save
      setTareaPunteos(prev => {
        const newState = { ...prev };
        delete newState[key];
        return newState;
      });

      showSnackbar(t('notas.scoreUpdatedSuccess'));
    } catch (error) {
      console.error('Error updating score:', error);
      const errorMessage = error.response?.data?.message || t('notas.errorUpdatingScore');
      showSnackbar(errorMessage, 'error');
      
      // Reset to original value on error
      setTareaPunteos(prev => {
        const newState = { ...prev };
        delete newState[key];
        return newState;
      });
    }
  };

  const handleDeleteTarea = async (cursoId, notaType, tareaId) => {
    try {
      await TareaService.delete(tareaId);
      
      // Reload tareas for this nota type
      const key = `${cursoId}_${notaType}`;
      if (notas[cursoId]?.id) {
        const response = await TareaService.getByNota(notas[cursoId].id, notaType);
        setTareas(prev => ({
          ...prev,
          [key]: response.data?.data || [],
        }));
      }

      // Reload notas to get updated values
      await loadNotasForCurrentStudent();

      showSnackbar(t('notas.tareaDeletedSuccess'));
    } catch (error) {
      console.error('Error deleting tarea:', error);
      showSnackbar(t('notas.errorDeletingTarea'), 'error');
    }
  };

  const savePendingPunteos = async () => {
    if (asignments.length === 0 || currentStudentIndex >= asignments.length) return;
    
    const currentAsignment = asignments[currentStudentIndex];
    if (!currentAsignment) return;
    
    // Get all nota IDs for the current student to ensure we only save for this student
    const currentStudentNotaIds = Object.values(notas)
      .filter(n => n.id)
      .map(n => n.id);
    
    // Save all pending punteos that haven't been saved yet, but only for current student
    const pendingPunteos = Object.keys(tareaPunteos);
    const savePromises = [];

    for (const key of pendingPunteos) {
      const [cursoId, tareaId] = key.split('_');
      const punteo = tareaPunteos[key];
      
      // Only save if this nota belongs to the current student
      if (!notas[cursoId]?.id || !currentStudentNotaIds.includes(notas[cursoId].id)) {
        continue; // Skip if nota not saved yet or doesn't belong to current student
      }
      
      // Find which nota type this tarea belongs to
      let notaType = null;
      for (const type of ['nota1', 'nota2', 'nota3', 'nota4']) {
        const tareaKey = `${cursoId}_${type}`;
        const cursoTareas = tareas[tareaKey] || [];
        if (cursoTareas.some(t => t.id === parseInt(tareaId))) {
          notaType = type;
          break;
        }
      }
      
      if (!notaType) continue;
      
      const punteoValue = punteo === '' || punteo === null || punteo === undefined 
        ? null 
        : parseFloat(punteo);
      
      if (punteoValue !== null && isNaN(punteoValue)) continue;
      
      // Use the current student's nota ID
      const notaId = notas[cursoId].id;
      
      savePromises.push(
        TareaService.updateStudentScore(parseInt(tareaId), notaId, punteoValue)
          .catch(error => {
            console.error(`Error saving punteo for tarea ${tareaId}, nota ${notaId}:`, error);
            return null; // Continue with other saves even if one fails
          })
      );
    }
    
    if (savePromises.length > 0) {
      await Promise.all(savePromises);
      // Small delay to ensure backend has finished recalculating
      await new Promise(resolve => setTimeout(resolve, 300));
    }
  };

  const handleSave = async () => {
    if (asignments.length === 0 || currentStudentIndex >= asignments.length) return;

    const currentAsignment = asignments[currentStudentIndex];
    setSaving(true);
    try {
      // First, save all pending punteos
      await savePendingPunteos();
      
      // Reload notas to get updated values after punteos are saved
      await loadNotasForCurrentStudent();
      
      // Now save the notas with updated values.
      // When detalle is true, use calculated tarea sums so we don't overwrite task scores with stale state.
      const notasArray = Object.values(notas)
        .filter(n => n.curso_id)
        .map(n => {
          const cursoId = n.curso_id;
          const useSums = n.detalle;
          const nota1 = useSums
            ? (calculateTareasSum(cursoId, 'nota1') ?? null)
            : (n.nota1 === '' || n.nota1 === null ? null : parseInt(n.nota1, 10));
          const nota2 = useSums
            ? (calculateTareasSum(cursoId, 'nota2') ?? null)
            : (n.nota2 === '' || n.nota2 === null ? null : parseInt(n.nota2, 10));
          const nota3 = useSums
            ? (calculateTareasSum(cursoId, 'nota3') ?? null)
            : (n.nota3 === '' || n.nota3 === null ? null : parseInt(n.nota3, 10));
          const nota4 = useSums
            ? (calculateTareasSum(cursoId, 'nota4') ?? null)
            : (n.nota4 === '' || n.nota4 === null ? null : parseInt(n.nota4, 10));
          return {
            curso_id: cursoId,
            nota1: nota1 != null ? parseInt(nota1, 10) : null,
            nota2: nota2 != null ? parseInt(nota2, 10) : null,
            nota3: nota3 != null ? parseInt(nota3, 10) : null,
            nota4: nota4 != null ? parseInt(nota4, 10) : null,
            detalle: n.detalle || false,
          };
        });
      await NotaService.create({
        asignment_id: currentAsignment.id,
        notas: notasArray,
        ausencias: currentAsignment.ausencias != null ? currentAsignment.ausencias : 0,
        reportes: currentAsignment.reportes != null ? currentAsignment.reportes : 0,
      });
      
      // Clear pending punteos after successful save
      setTareaPunteos({});
      
      // Reload everything to get final updated values
      await loadNotasForCurrentStudent();
      
      showSnackbar(t('notas.notasSavedSuccess'));
    } catch (error) {
      console.error('Error saving notas:', error);
      const errorMessage = error.response?.data?.message || t('notas.errorSavingNotas');
      showSnackbar(errorMessage, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handlePreviousStudent = () => {
    if (currentStudentIndex > 0) {
      setCurrentStudentIndex(currentStudentIndex - 1);
    }
  };

  const handleNextStudent = () => {
    if (currentStudentIndex < asignments.length - 1) {
      setCurrentStudentIndex(currentStudentIndex + 1);
    }
  };

  const handleClassModeNotaChange = (asignmentId, field, value) => {
    setClassModeNotasByAsignment((prev) => {
      const current = prev[asignmentId] || {};
      return {
        ...prev,
        [asignmentId]: { ...current, [field]: value },
      };
    });
  };

  const handleAsignmentMetaChange = (asignmentId, field, value) => {
    const num = value === '' || value === null ? null : parseInt(value, 10);
    setAsignments((prev) =>
      prev.map((a) =>
        a.id === asignmentId ? { ...a, [field]: num !== null && !isNaN(num) ? num : 0 } : a
      )
    );
  };

  const saveClassModePendingPunteos = async () => {
    const pendingKeys = Object.keys(classModeTareaPunteos);
    if (pendingKeys.length === 0) return;
    for (const key of pendingKeys) {
      const underscoreIdx = key.indexOf('_');
      const asignmentId = parseInt(key.slice(0, underscoreIdx), 10);
      const tareaId = parseInt(key.slice(underscoreIdx + 1), 10);
      const nota = classModeNotasByAsignment[asignmentId];
      if (!nota?.id) continue;
      const punteo = classModeTareaPunteos[key];
      const punteoValue = punteo === '' || punteo === null || punteo === undefined ? null : parseFloat(punteo);
      if (punteoValue !== null && isNaN(punteoValue)) continue;
      try {
        await TareaService.updateStudentScore(tareaId, nota.id, punteoValue);
      } catch (e) {
        console.error('Error saving class mode punteo:', e);
      }
    }
    await new Promise((r) => setTimeout(r, 300));
    setClassModeTareaPunteos({});
    // Reload class mode notas so displayed sums are up to date
    const byAsignment = {};
    for (const asignment of asignments) {
      const response = await NotaService.getNotasByAsignment(asignment.id);
      const existingNotas = response.data?.data || [];
      const nota = existingNotas.find((n) => n.curso_id === parseInt(selectedCursoForClass, 10));
      if (nota) {
        byAsignment[asignment.id] = {
          id: nota.id,
          curso_id: nota.curso_id,
          nota1: nota.nota1 ?? '',
          nota2: nota.nota2 ?? '',
          nota3: nota.nota3 ?? '',
          nota4: nota.nota4 ?? '',
          detalle: nota.detalle || false,
        };
      } else {
        byAsignment[asignment.id] = {
          curso_id: parseInt(selectedCursoForClass, 10),
          nota1: '',
          nota2: '',
          nota3: '',
          nota4: '',
          detalle: false,
        };
      }
    }
    setClassModeNotasByAsignment(byAsignment);
  };

  const handleClassModeSave = async () => {
    if (!selectedCursoForClass || asignments.length === 0) return;
    setSaving(true);
    try {
      await saveClassModePendingPunteos();
      for (const asignment of asignments) {
        const notaData = classModeNotasByAsignment[asignment.id];
        if (!notaData) continue;
        const response = await NotaService.getNotasByAsignment(asignment.id);
        const existingNotas = response.data?.data || [];
        const otherNotas = existingNotas
          .filter((n) => n.curso_id !== parseInt(selectedCursoForClass, 10))
          .map((n) => ({
            curso_id: n.curso_id,
            nota1: n.nota1,
            nota2: n.nota2,
            nota3: n.nota3,
            nota4: n.nota4,
            detalle: n.detalle || false,
          }));
        const thisNota = {
          curso_id: parseInt(selectedCursoForClass, 10),
          nota1: notaData.nota1 === '' || notaData.nota1 === null ? null : parseInt(notaData.nota1),
          nota2: notaData.nota2 === '' || notaData.nota2 === null ? null : parseInt(notaData.nota2),
          nota3: notaData.nota3 === '' || notaData.nota3 === null ? null : parseInt(notaData.nota3),
          nota4: notaData.nota4 === '' || notaData.nota4 === null ? null : parseInt(notaData.nota4),
          detalle: notaData.detalle || false,
        };
        const notasArray = [...otherNotas, thisNota];
        await NotaService.create({
          asignment_id: asignment.id,
          notas: notasArray,
          ausencias: asignment.ausencias != null ? asignment.ausencias : 0,
          reportes: asignment.reportes != null ? asignment.reportes : 0,
        });
      }
      showSnackbar(t('notas.notasSavedSuccess'));
      // Reload class mode notas
      const byAsignment = {};
      await Promise.all(
        asignments.map(async (asignment) => {
          const response = await NotaService.getNotasByAsignment(asignment.id);
          const existingNotas = response.data?.data || [];
          const nota = existingNotas.find(
            (n) => n.curso_id === parseInt(selectedCursoForClass, 10)
          );
          if (nota) {
            byAsignment[asignment.id] = {
              id: nota.id,
              curso_id: nota.curso_id,
              nota1: nota.nota1 ?? '',
              nota2: nota.nota2 ?? '',
              nota3: nota.nota3 ?? '',
              nota4: nota.nota4 ?? '',
              detalle: nota.detalle || false,
            };
          } else {
            byAsignment[asignment.id] = {
              curso_id: parseInt(selectedCursoForClass, 10),
              nota1: '',
              nota2: '',
              nota3: '',
              nota4: '',
              detalle: false,
            };
          }
        })
      );
      setClassModeNotasByAsignment(byAsignment);
    } catch (error) {
      console.error('Error saving class mode notas:', error);
      showSnackbar(error.response?.data?.message || t('notas.errorSavingNotas'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const calculateClassModeTareasSum = (asignmentId, notaType) => {
    const key = `${asignmentId}_${notaType}`;
    const cursoTareas = classModeTareas[key] || [];
    let sum = 0;
    cursoTareas.forEach((tarea) => {
      const punteoKey = `${asignmentId}_${tarea.id}`;
      const pendingPunteo = classModeTareaPunteos[punteoKey];
      if (pendingPunteo !== undefined && pendingPunteo !== '' && pendingPunteo !== null) {
        const v = parseFloat(pendingPunteo);
        if (!isNaN(v)) sum += v;
      } else if (tarea.punteo != null) {
        const v = parseFloat(tarea.punteo);
        if (!isNaN(v)) sum += v;
      }
    });
    return sum > 0 ? Math.round(sum) : null;
  };

  const toggleClassModeTareasExpansion = async (asignmentId, notaType) => {
    const key = `${asignmentId}_${notaType}`;
    const isExpanded = classModeExpandedTareas[key];
    setClassModeExpandedTareas((prev) => ({ ...prev, [key]: !isExpanded }));

    if (!isExpanded) {
      const nota = classModeNotasByAsignment[asignmentId];
      if (!nota?.id) return;
      try {
        const response = await TareaService.getByNota(nota.id, notaType);
        setClassModeTareas((prev) => ({
          ...prev,
          [key]: response.data?.data || [],
        }));
      } catch (error) {
        console.error('Error loading class mode tareas:', error);
        showSnackbar(t('notas.errorLoadingTareas'), 'error');
      }
    }
  };

  const handleClassModeTareaPunteoChange = (asignmentId, tareaId, punteo) => {
    setClassModeTareaPunteos((prev) => ({
      ...prev,
      [`${asignmentId}_${tareaId}`]: punteo,
    }));
  };

  const handleClassModeTareaPunteoBlur = async (asignmentId, tareaId, notaType) => {
    const nota = classModeNotasByAsignment[asignmentId];
    if (!nota?.id) {
      showSnackbar(t('notas.pleaseSaveNotaFirst'), 'error');
      return;
    }
    const punteoKey = `${asignmentId}_${tareaId}`;
    const punteo = classModeTareaPunteos[punteoKey];
    const punteoValue = punteo === '' || punteo === null || punteo === undefined ? null : parseFloat(punteo);
    if (punteoValue !== null && isNaN(punteoValue)) {
      showSnackbar(t('notas.errorUpdatingScore'), 'error');
      setClassModeTareaPunteos((prev) => {
        const next = { ...prev };
        delete next[punteoKey];
        return next;
      });
      return;
    }
    try {
      await TareaService.updateStudentScore(tareaId, nota.id, punteoValue);
      await new Promise((r) => setTimeout(r, 300));
      const key = `${asignmentId}_${notaType}`;
      const response = await TareaService.getByNota(nota.id, notaType);
      setClassModeTareas((prev) => ({ ...prev, [key]: response.data?.data || [] }));
      const updated = await NotaService.getNotasByAsignment(asignmentId);
      const allNotas = updated.data?.data || [];
      const n = allNotas.find((x) => x.curso_id === parseInt(selectedCursoForClass, 10));
      if (n) {
        setClassModeNotasByAsignment((prev) => ({
          ...prev,
          [asignmentId]: {
            id: n.id,
            curso_id: n.curso_id,
            nota1: n.nota1 ?? '',
            nota2: n.nota2 ?? '',
            nota3: n.nota3 ?? '',
            nota4: n.nota4 ?? '',
            detalle: n.detalle || false,
          },
        }));
      }
      setClassModeTareaPunteos((prev) => {
        const next = { ...prev };
        delete next[punteoKey];
        return next;
      });
      showSnackbar(t('notas.scoreUpdatedSuccess'));
    } catch (error) {
      showSnackbar(error.response?.data?.message || t('notas.errorUpdatingScore'), 'error');
      setClassModeTareaPunteos((prev) => {
        const next = { ...prev };
        delete next[punteoKey];
        return next;
      });
    }
  };

  const handleClassModeTareaFormChange = (key, field, value) => {
    setClassModeTareaForm((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: value },
    }));
  };

  const handleClassModeCreateTarea = async (asignmentId, notaType) => {
    const key = `class_${asignmentId}_${notaType}`;
    const formData = classModeTareaForm[key] || {};
    if (!formData.name || formData.total === undefined) {
      showSnackbar(t('notas.pleaseFillAllFields'), 'error');
      return;
    }
    try {
      await TareaService.create({
        grade_id: selectedGrade,
        cycle_id: selectedCycle,
        curso_id: parseInt(selectedCursoForClass, 10),
        name: formData.name,
        nota: notaType,
        total: parseFloat(formData.total),
      });
      const updates = await Promise.all(
        asignments.map(async (a) => {
          const n = classModeNotasByAsignment[a.id];
          if (!n?.id) return null;
          const response = await TareaService.getByNota(n.id, notaType);
          return { key: `${a.id}_${notaType}`, data: response.data?.data || [] };
        })
      );
      setClassModeTareas((prev) => {
        const next = { ...prev };
        updates.forEach((u) => {
          if (u) next[u.key] = u.data;
        });
        return next;
      });
      setClassModeTareaForm((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      showSnackbar(t('notas.tareaCreatedSuccess'));
      const updated = await NotaService.getNotasByAsignment(asignmentId);
      const allNotas = updated.data?.data || [];
      const n = allNotas.find((x) => x.curso_id === parseInt(selectedCursoForClass, 10));
      if (n) {
        setClassModeNotasByAsignment((prev) => ({
          ...prev,
          [asignmentId]: {
            id: n.id,
            curso_id: n.curso_id,
            nota1: n.nota1 ?? '',
            nota2: n.nota2 ?? '',
            nota3: n.nota3 ?? '',
            nota4: n.nota4 ?? '',
            detalle: n.detalle || false,
          },
        }));
      }
    } catch (error) {
      showSnackbar(t('notas.errorCreatingTarea'), 'error');
    }
  };

  const handleClassModeDeleteTarea = async (asignmentId, notaType, tareaId) => {
    try {
      await TareaService.delete(tareaId);
      const nota = classModeNotasByAsignment[asignmentId];
      if (nota?.id) {
        const response = await TareaService.getByNota(nota.id, notaType);
        setClassModeTareas((prev) => ({
          ...prev,
          [`${asignmentId}_${notaType}`]: response.data?.data || [],
        }));
      }
      const updated = await NotaService.getNotasByAsignment(asignmentId);
      const allNotas = updated.data?.data || [];
      const n = allNotas.find((x) => x.curso_id === parseInt(selectedCursoForClass, 10));
      if (n) {
        setClassModeNotasByAsignment((prev) => ({
          ...prev,
          [asignmentId]: {
            id: n.id,
            curso_id: n.curso_id,
            nota1: n.nota1 ?? '',
            nota2: n.nota2 ?? '',
            nota3: n.nota3 ?? '',
            nota4: n.nota4 ?? '',
            detalle: n.detalle || false,
          },
        }));
      }
      showSnackbar(t('notas.tareaDeletedSuccess'));
    } catch (error) {
      showSnackbar(t('notas.errorDeletingTarea'), 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const currentAsignment = asignments[currentStudentIndex];
  const currentStudent = currentAsignment?.student;
  const currentGrade = currentAsignment?.grade;

  // Check if we should show input view based on URL params
  const asignmentIdFromUrl = searchParams.get('asignment_id');

  if (tabValue === 1) {
    return (
      <Box>
        <Card sx={{ mb: 2 }}>
          <CardHeader
            title={
              <Typography variant="h5">{t('notas.title')}</Typography>
            }
            action={
              <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                <Tab label={t('notas.input')} />
                <Tab label={t('notas.list')} />
              </Tabs>
            }
          />
        </Card>
        <NotasList />
      </Box>
    );
  }

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardHeader
          title={
            <Typography variant="h5">{t('notas.title')}</Typography>
          }
          action={
            <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
              <Tab label={t('notas.input')} />
              <Tab label={t('notas.list')} />
            </Tabs>
          }
        />
        <CardContent>
          <Box display="flex" flexDirection="column" gap={2}>
            <ToggleButtonGroup
              value={inputMode}
              exclusive
              onChange={(e, newMode) => newMode && setInputMode(newMode)}
              aria-label="input mode"
            >
              <ToggleButton value="grade" aria-label="by grade">
                {t('notas.byGrade')}
              </ToggleButton>
              <ToggleButton value="class" aria-label="by class">
                {t('notas.byClass')}
              </ToggleButton>
            </ToggleButtonGroup>

            {inputMode === 'grade' && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>{t('students.grade')}</InputLabel>
                    <Select
                      value={selectedGrade}
                      label={t('students.grade')}
                      onChange={(e) => setSelectedGrade(e.target.value)}
                    >
                      {grades.map((grade) => (
                        <MenuItem key={grade.id} value={grade.id}>
                          {grade.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>{t('students.cycle')}</InputLabel>
                    <Select
                      value={selectedCycle}
                      label={t('students.cycle')}
                      onChange={(e) => setSelectedCycle(e.target.value)}
                    >
                      {cycles.map((cycle) => (
                        <MenuItem key={cycle.id} value={cycle.id}>
                          {cycle.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            )}

            {inputMode === 'class' && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth required>
                    <InputLabel>{t('students.grade')}</InputLabel>
                    <Select
                      value={selectedGrade}
                      label={t('students.grade')}
                      onChange={(e) => setSelectedGrade(e.target.value)}
                    >
                      {grades.map((grade) => (
                        <MenuItem key={grade.id} value={grade.id}>
                          {grade.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth required>
                    <InputLabel>{t('students.cycle')}</InputLabel>
                    <Select
                      value={selectedCycle}
                      label={t('students.cycle')}
                      onChange={(e) => setSelectedCycle(e.target.value)}
                    >
                      {cycles.map((cycle) => (
                        <MenuItem key={cycle.id} value={cycle.id}>
                          {cycle.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>{t('notas.curso')}</InputLabel>
                    <Select
                      value={selectedCursoForClass}
                      label={t('notas.curso')}
                      onChange={(e) => setSelectedCursoForClass(e.target.value)}
                    >
                      {classModeCursos.map((curso) => (
                        <MenuItem key={curso.id} value={curso.id}>
                          {curso.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            )}
          </Box>
        </CardContent>
      </Card>

      {inputMode === 'class' && selectedCursoForClass && asignments.length > 0 && (
        <Card sx={{ mt: 2 }}>
          <CardHeader
            title={`${classModeCursos.find((c) => c.id === parseInt(selectedCursoForClass, 10))?.name || t('notas.curso')} – ${t('notas.studentsListTitle')}`}
            action={
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleClassModeSave}
                disabled={saving}
              >
                {t('notas.saveNotas')}
              </Button>
            }
          />
          <CardContent>
            {classModeLoading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('notas.name')}</TableCell>
                      <TableCell align="center">{t('notas.ausencias')}</TableCell>
                      <TableCell align="center">{t('notas.reportes')}</TableCell>
                      <TableCell align="center">{t('notas.nota1')}</TableCell>
                      <TableCell align="center">{t('notas.nota2')}</TableCell>
                      <TableCell align="center">{t('notas.nota3')}</TableCell>
                      <TableCell align="center">{t('notas.nota4')}</TableCell>
                      <TableCell align="center">{t('notas.detalle')}</TableCell>
                      <TableCell align="center">{t('notas.tareas')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {asignments.map((asignment) => {
                      const student = asignment.student;
                      const nota = classModeNotasByAsignment[asignment.id] || {};
                      const isDetalle = nota.detalle;
                      const tareasLoaded = ['nota1', 'nota2', 'nota3', 'nota4'].some(
                        (type) => classModeTareas[`${asignment.id}_${type}`] !== undefined
                      );
                      const nota1Sum = calculateClassModeTareasSum(asignment.id, 'nota1');
                      const nota2Sum = calculateClassModeTareasSum(asignment.id, 'nota2');
                      const nota3Sum = calculateClassModeTareasSum(asignment.id, 'nota3');
                      const nota4Sum = calculateClassModeTareasSum(asignment.id, 'nota4');
                      const nota1Val = isDetalle
                        ? (tareasLoaded && nota1Sum !== null ? nota1Sum : (nota.nota1 ?? ''))
                        : (nota.nota1 ?? '');
                      const nota2Val = isDetalle
                        ? (tareasLoaded && nota2Sum !== null ? nota2Sum : (nota.nota2 ?? ''))
                        : (nota.nota2 ?? '');
                      const nota3Val = isDetalle
                        ? (tareasLoaded && nota3Sum !== null ? nota3Sum : (nota.nota3 ?? ''))
                        : (nota.nota3 ?? '');
                      const nota4Val = isDetalle
                        ? (tareasLoaded && nota4Sum !== null ? nota4Sum : (nota.nota4 ?? ''))
                        : (nota.nota4 ?? '');
                      return (
                        <React.Fragment key={asignment.id}>
                          <TableRow hover>
                            <TableCell>
                              <Typography fontWeight={500}>
                                {student?.name} ({student?.code})
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                type="number"
                                size="small"
                                value={asignment.ausencias ?? ''}
                                onChange={(e) => handleAsignmentMetaChange(asignment.id, 'ausencias', e.target.value)}
                                inputProps={{ min: 0 }}
                                sx={{ width: 72 }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                type="number"
                                size="small"
                                value={asignment.reportes ?? ''}
                                onChange={(e) => handleAsignmentMetaChange(asignment.id, 'reportes', e.target.value)}
                                inputProps={{ min: 0 }}
                                sx={{ width: 72 }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                type="number"
                                size="small"
                                value={nota1Val}
                                onChange={(e) => handleClassModeNotaChange(asignment.id, 'nota1', e.target.value)}
                                inputProps={{ min: 0, max: 100 }}
                                sx={{ width: 64 }}
                                disabled={isDetalle}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                type="number"
                                size="small"
                                value={nota2Val}
                                onChange={(e) => handleClassModeNotaChange(asignment.id, 'nota2', e.target.value)}
                                inputProps={{ min: 0, max: 100 }}
                                sx={{ width: 64 }}
                                disabled={isDetalle}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                type="number"
                                size="small"
                                value={nota3Val}
                                onChange={(e) => handleClassModeNotaChange(asignment.id, 'nota3', e.target.value)}
                                inputProps={{ min: 0, max: 100 }}
                                sx={{ width: 64 }}
                                disabled={isDetalle}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                type="number"
                                size="small"
                                value={nota4Val}
                                onChange={(e) => handleClassModeNotaChange(asignment.id, 'nota4', e.target.value)}
                                inputProps={{ min: 0, max: 100 }}
                                sx={{ width: 64 }}
                                disabled={isDetalle}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={nota.detalle ? t('notas.yes') : t('notas.no')}
                                color={nota.detalle ? 'success' : 'default'}
                                size="small"
                                onClick={() => handleClassModeNotaChange(asignment.id, 'detalle', !nota.detalle)}
                                sx={{ cursor: 'pointer' }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                size="small"
                                onClick={async () => {
                                  const overallKey = `${asignment.id}_tareas`;
                                  const newExpanded = !classModeExpandedTareas[overallKey];
                                  setClassModeExpandedTareas((prev) => {
                                    const next = { ...prev, [overallKey]: newExpanded };
                                    ['nota1', 'nota2', 'nota3', 'nota4'].forEach((notaType) => {
                                      next[`${asignment.id}_${notaType}`] = newExpanded;
                                    });
                                    return next;
                                  });
                                  if (newExpanded && nota?.id) {
                                    await Promise.all(
                                      ['nota1', 'nota2', 'nota3', 'nota4'].map(async (notaType) => {
                                        const k = `${asignment.id}_${notaType}`;
                                        if (classModeTareas[k]) return;
                                        const res = await TareaService.getByNota(nota.id, notaType);
                                        setClassModeTareas((prev) => ({
                                          ...prev,
                                          [k]: res.data?.data || [],
                                        }));
                                      })
                                    );
                                  }
                                }}
                                color={classModeExpandedTareas[`${asignment.id}_tareas`] ? 'primary' : 'default'}
                                disabled={!nota?.id || !isDetalle}
                                title={t('notas.viewTareas')}
                              >
                                <AssignmentIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                          {classModeExpandedTareas[`${asignment.id}_tareas`] &&
                            nota?.id &&
                            ['nota1', 'nota2', 'nota3', 'nota4'].map((notaType) => {
                              const key = `${asignment.id}_${notaType}`;
                              const formKey = `class_${asignment.id}_${notaType}`;
                              const cursoTareas = classModeTareas[key] || [];
                              const totalTareas = cursoTareas.length;
                              return (
                                <TableRow key={key}>
                                  <TableCell colSpan={7} sx={{ py: 2, backgroundColor: 'grey.50' }}>
                                    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'space-between',
                                          px: 2,
                                          py: 1.5,
                                          cursor: 'pointer',
                                          backgroundColor: 'common.white',
                                        }}
                                        onClick={() => toggleClassModeTareasExpansion(asignment.id, notaType)}
                                      >
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                          {t('notas.tareasFor', { notaType: notaType.toUpperCase() })} ({totalTareas})
                                        </Typography>
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            toggleClassModeTareasExpansion(asignment.id, notaType);
                                          }}
                                          title={classModeExpandedTareas[key] ? t('notas.collapse') : t('notas.expand')}
                                        >
                                          {classModeExpandedTareas[key] ? (
                                            <ExpandLessIcon fontSize="small" />
                                          ) : (
                                            <ExpandMoreIcon fontSize="small" />
                                          )}
                                        </IconButton>
                                      </Box>
                                      <Collapse in={!!classModeExpandedTareas[key]} timeout="auto" unmountOnExit>
                                        <Box sx={{ px: 2, pb: 2 }}>
                                          {cursoTareas.length > 0 && (
                                            <TableContainer component={Paper} sx={{ mb: 2 }}>
                                              <Table size="small">
                                                <TableHead>
                                                  <TableRow>
                                                    <TableCell>{t('notas.name')}</TableCell>
                                                    <TableCell align="right">{t('notas.total')}</TableCell>
                                                    <TableCell align="right">{t('notas.yourPunteo')}</TableCell>
                                                    <TableCell align="center">{t('common.actions')}</TableCell>
                                                  </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                  {cursoTareas.map((tarea) => (
                                                    <TableRow key={tarea.id}>
                                                      <TableCell>{tarea.name}</TableCell>
                                                      <TableCell align="right">{tarea.total}</TableCell>
                                                      <TableCell align="right">
                                                        <TextField
                                                          type="number"
                                                          size="small"
                                                          value={
                                                            classModeTareaPunteos[`${asignment.id}_${tarea.id}`] !== undefined
                                                              ? classModeTareaPunteos[`${asignment.id}_${tarea.id}`]
                                                              : (tarea.punteo ?? '')
                                                          }
                                                          onChange={(e) =>
                                                            handleClassModeTareaPunteoChange(asignment.id, tarea.id, e.target.value)
                                                          }
                                                          onBlur={() =>
                                                            handleClassModeTareaPunteoBlur(asignment.id, tarea.id, notaType)
                                                          }
                                                          inputProps={{
                                                            min: 0,
                                                            max: tarea.total,
                                                            step: 0.01,
                                                            style: { textAlign: 'right', width: 80 },
                                                          }}
                                                          sx={{ width: 100 }}
                                                        />
                                                      </TableCell>
                                                      <TableCell align="center">
                                                        <IconButton
                                                          size="small"
                                                          color="error"
                                                          onClick={() =>
                                                            handleClassModeDeleteTarea(asignment.id, notaType, tarea.id)
                                                          }
                                                        >
                                                          <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                      </TableCell>
                                                    </TableRow>
                                                  ))}
                                                </TableBody>
                                              </Table>
                                            </TableContainer>
                                          )}
                                          <Paper sx={{ p: 2 }}>
                                            <Typography variant="subtitle2" gutterBottom>
                                              {t('notas.createNewTarea', { notaType: notaType.toUpperCase() })}
                                            </Typography>
                                            <Grid container spacing={2} alignItems="center">
                                              <Grid item xs={12} sm={4}>
                                                <TextField
                                                  fullWidth
                                                  size="small"
                                                  label={t('notas.name')}
                                                  value={classModeTareaForm[formKey]?.name || ''}
                                                  onChange={(e) =>
                                                    handleClassModeTareaFormChange(formKey, 'name', e.target.value)
                                                  }
                                                />
                                              </Grid>
                                              <Grid item xs={12} sm={3}>
                                                <TextField
                                                  fullWidth
                                                  size="small"
                                                  type="number"
                                                  label={t('notas.totalPoints')}
                                                  value={classModeTareaForm[formKey]?.total || ''}
                                                  onChange={(e) =>
                                                    handleClassModeTareaFormChange(formKey, 'total', e.target.value)
                                                  }
                                                  inputProps={{ min: 0, step: 0.01 }}
                                                />
                                              </Grid>
                                              <Grid item xs={12} sm={5}>
                                                <Button
                                                  variant="contained"
                                                  size="small"
                                                  startIcon={<AddIcon />}
                                                  onClick={() => handleClassModeCreateTarea(asignment.id, notaType)}
                                                >
                                                  {t('notas.addTarea')}
                                                </Button>
                                              </Grid>
                                            </Grid>
                                          </Paper>
                                        </Box>
                                      </Collapse>
                                    </Paper>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {inputMode === 'grade' && currentAsignment && currentStudent && (
        <Card>
          <CardHeader
            title={
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box display="flex" alignItems="center" gap={2}>
                  <IconButton
                    onClick={handlePreviousStudent}
                    disabled={currentStudentIndex === 0}
                    color="primary"
                  >
                    <ArrowBackIcon />
                  </IconButton>
                  <Box>
                    <Typography variant="h6">
                      {currentStudent.name} ({currentStudent.code})
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {currentGrade?.name} - {asignments[currentStudentIndex]?.cycle?.name}
                    </Typography>
                  </Box>
                  <IconButton
                    onClick={handleNextStudent}
                    disabled={currentStudentIndex === asignments.length - 1}
                    color="primary"
                  >
                    <ArrowForwardIcon />
                  </IconButton>
                </Box>
                <Chip
                  label={`${currentStudentIndex + 1} / ${asignments.length}`}
                  color="primary"
                />
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={saving}
                >
                  {t('notas.saveNotas')}
                </Button>
              </Box>
            }
          />
          <CardContent>
            {loading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                  <TextField
                    type="number"
                    size="small"
                    label={t('notas.ausencias')}
                    value={currentAsignment?.ausencias ?? ''}
                    onChange={(e) => handleAsignmentMetaChange(currentAsignment?.id, 'ausencias', e.target.value)}
                    inputProps={{ min: 0 }}
                    sx={{ width: 120 }}
                  />
                  <TextField
                    type="number"
                    size="small"
                    label={t('notas.reportes')}
                    value={currentAsignment?.reportes ?? ''}
                    onChange={(e) => handleAsignmentMetaChange(currentAsignment?.id, 'reportes', e.target.value)}
                    inputProps={{ min: 0 }}
                    sx={{ width: 120 }}
                  />
                </Box>
                <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('notas.curso')}</TableCell>
                      <TableCell align="center">{t('notas.nota1')}</TableCell>
                      <TableCell align="center">{t('notas.nota2')}</TableCell>
                      <TableCell align="center">{t('notas.nota3')}</TableCell>
                      <TableCell align="center">{t('notas.nota4')}</TableCell>
                      <TableCell align="center">{t('notas.detalle')}</TableCell>
                      <TableCell align="center">{t('notas.tareas')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cursos.map((curso) => {
                      const isDetalle = notas[curso.id]?.detalle;
                      
                      // Calculate sums for each nota type from tareas (if tareas are loaded)
                      const nota1Sum = calculateTareasSum(curso.id, 'nota1');
                      const nota2Sum = calculateTareasSum(curso.id, 'nota2');
                      const nota3Sum = calculateTareasSum(curso.id, 'nota3');
                      const nota4Sum = calculateTareasSum(curso.id, 'nota4');
                      
                      // Check if tareas are loaded for this curso
                      const tareasLoaded = ['nota1', 'nota2', 'nota3', 'nota4'].some(type => {
                        const key = `${curso.id}_${type}`;
                        return tareas[key] !== undefined;
                      });
                      
                      // When detalle is true: 
                      //   - If tareas are loaded: use calculated sum from tareas
                      //   - If tareas not loaded yet: use saved nota value (backend-calculated sum)
                      // When detalle is false: show saved value if exists, otherwise show calculated sum
                      const savedNota1 = notas[curso.id]?.nota1;
                      const savedNota2 = notas[curso.id]?.nota2;
                      const savedNota3 = notas[curso.id]?.nota3;
                      const savedNota4 = notas[curso.id]?.nota4;
                      
                      const nota1Value = isDetalle 
                        ? (tareasLoaded && nota1Sum !== null ? nota1Sum : (savedNota1 !== null && savedNota1 !== '' ? savedNota1 : ''))
                        : (savedNota1 !== null && savedNota1 !== '' ? savedNota1 : (nota1Sum !== null ? nota1Sum : ''));
                      const nota2Value = isDetalle 
                        ? (tareasLoaded && nota2Sum !== null ? nota2Sum : (savedNota2 !== null && savedNota2 !== '' ? savedNota2 : ''))
                        : (savedNota2 !== null && savedNota2 !== '' ? savedNota2 : (nota2Sum !== null ? nota2Sum : ''));
                      const nota3Value = isDetalle 
                        ? (tareasLoaded && nota3Sum !== null ? nota3Sum : (savedNota3 !== null && savedNota3 !== '' ? savedNota3 : ''))
                        : (savedNota3 !== null && savedNota3 !== '' ? savedNota3 : (nota3Sum !== null ? nota3Sum : ''));
                      const nota4Value = isDetalle 
                        ? (tareasLoaded && nota4Sum !== null ? nota4Sum : (savedNota4 !== null && savedNota4 !== '' ? savedNota4 : ''))
                        : (savedNota4 !== null && savedNota4 !== '' ? savedNota4 : (nota4Sum !== null ? nota4Sum : ''));
                      
                      return (
                        <React.Fragment key={curso.id}>
                          <TableRow hover>
                            <TableCell>
                              <Typography fontWeight="medium">{curso.name}</Typography>
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                type="number"
                                size="small"
                                value={nota1Value}
                                onChange={(e) => handleNotaChange(curso.id, 'nota1', e.target.value)}
                                inputProps={{ min: 0, max: 100, style: { textAlign: 'center' } }}
                                sx={{ width: 80 }}
                                disabled={isDetalle}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                type="number"
                                size="small"
                                value={nota2Value}
                                onChange={(e) => handleNotaChange(curso.id, 'nota2', e.target.value)}
                                inputProps={{ min: 0, max: 100, style: { textAlign: 'center' } }}
                                sx={{ width: 80 }}
                                disabled={isDetalle}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                type="number"
                                size="small"
                                value={nota3Value}
                                onChange={(e) => handleNotaChange(curso.id, 'nota3', e.target.value)}
                                inputProps={{ min: 0, max: 100, style: { textAlign: 'center' } }}
                                sx={{ width: 80 }}
                                disabled={isDetalle}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                type="number"
                                size="small"
                                value={nota4Value}
                                onChange={(e) => handleNotaChange(curso.id, 'nota4', e.target.value)}
                                inputProps={{ min: 0, max: 100, style: { textAlign: 'center' } }}
                                sx={{ width: 80 }}
                                disabled={isDetalle}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={isDetalle ? t('notas.yes') : t('notas.no')}
                                color={isDetalle ? 'success' : 'default'}
                                size="small"
                                onClick={() => handleNotaChange(curso.id, 'detalle', !isDetalle)}
                                sx={{ cursor: 'pointer' }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                size="small"
                                onClick={async () => {
                                  const overallKey = `${curso.id}_tareas`;
                                  const newExpanded = !expandedTareas[overallKey];

                                  // Toggle overall + all nota sections
                                  setExpandedTareas(prev => {
                                    const next = { ...prev, [overallKey]: newExpanded };
                                    ['nota1', 'nota2', 'nota3', 'nota4'].forEach(notaType => {
                                      next[`${curso.id}_${notaType}`] = newExpanded;
                                    });
                                    return next;
                                  });

                                  // Load tareas for all nota types when expanding (no extra toggling)
                                  if (newExpanded && notas[curso.id]?.id && selectedGrade && selectedCycle) {
                                    await Promise.all(
                                      ['nota1', 'nota2', 'nota3', 'nota4'].map(async (notaType) => {
                                        const k = `${curso.id}_${notaType}`;
                                        if (tareas[k]) return;
                                        const response = await TareaService.getByNota(notas[curso.id].id, notaType);
                                        setTareas(prev => ({
                                          ...prev,
                                          [k]: response.data?.data || [],
                                        }));
                                      })
                                    );
                                  }
                                }}
                                color={expandedTareas[`${curso.id}_tareas`] ? 'primary' : 'default'}
                                disabled={!notas[curso.id]?.id || !selectedGrade || !selectedCycle || !isDetalle}
                                title={t('notas.viewTareas')}
                              >
                                <AssignmentIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                          {/* Show tareas for each nota type */}
                          {expandedTareas[`${curso.id}_tareas`] && notas[curso.id]?.id && ['nota1', 'nota2', 'nota3', 'nota4'].map((notaType) => {
                            const key = `${curso.id}_${notaType}`;
                            const cursoTareas = tareas[key] || [];
                            const totalTareas = cursoTareas.length;
                            
                            return (
                              <TableRow key={key}>
                                <TableCell colSpan={7} sx={{ py: 2, backgroundColor: 'grey.50' }}>
                                  <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        px: 2,
                                        py: 1.5,
                                        cursor: 'pointer',
                                        backgroundColor: 'common.white',
                                      }}
                                      onClick={() => toggleTareasExpansion(curso.id, notaType)}
                                    >
                                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                        {t('notas.tareasFor', { notaType: notaType.toUpperCase() })} ({totalTareas})
                                      </Typography>
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleTareasExpansion(curso.id, notaType);
                                        }}
                                        title={expandedTareas[key] ? t('notas.collapse') : t('notas.expand')}
                                      >
                                        {expandedTareas[key] ? (
                                          <ExpandLessIcon fontSize="small" />
                                        ) : (
                                          <ExpandMoreIcon fontSize="small" />
                                        )}
                                      </IconButton>
                                    </Box>

                                    <Collapse in={!!expandedTareas[key]} timeout="auto" unmountOnExit>
                                      <Box sx={{ px: 2, pb: 2 }}>
                                        {/* Tareas List */}
                                        {cursoTareas.length > 0 && (
                                          <TableContainer component={Paper} sx={{ mb: 2 }}>
                                            <Table size="small">
                                              <TableHead>
                                                <TableRow>
                                                  <TableCell>{t('notas.name')}</TableCell>
                                                  <TableCell align="right">{t('notas.total')}</TableCell>
                                                  <TableCell align="right">{t('notas.yourPunteo')}</TableCell>
                                                  <TableCell align="center">{t('common.actions')}</TableCell>
                                                </TableRow>
                                              </TableHead>
                                              <TableBody>
                                                {cursoTareas.map((tarea) => (
                                                  <TableRow key={tarea.id}>
                                                    <TableCell>{tarea.name}</TableCell>
                                                    <TableCell align="right">{tarea.total}</TableCell>
                                                    <TableCell align="right">
                                                      <TextField
                                                        type="number"
                                                        size="small"
                                                        value={tareaPunteos[`${curso.id}_${tarea.id}`] !== undefined 
                                                          ? tareaPunteos[`${curso.id}_${tarea.id}`] 
                                                          : (tarea.punteo || '')}
                                                        onChange={(e) =>
                                                          handleTareaPunteoChange(curso.id, tarea.id, e.target.value)
                                                        }
                                                        onBlur={() => handleTareaPunteoBlur(curso.id, tarea.id, notaType)}
                                                        inputProps={{
                                                          min: 0,
                                                          max: tarea.total,
                                                          step: 0.01,
                                                          style: { textAlign: 'right', width: 80 },
                                                        }}
                                                        sx={{ width: 100 }}
                                                      />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                      <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleDeleteTarea(curso.id, notaType, tarea.id)}
                                                      >
                                                        <DeleteIcon fontSize="small" />
                                                      </IconButton>
                                                    </TableCell>
                                                  </TableRow>
                                                ))}
                                              </TableBody>
                                            </Table>
                                          </TableContainer>
                                        )}

                                        {/* Create Tarea Form */}
                                        <Paper sx={{ p: 2 }}>
                                          <Typography variant="subtitle2" gutterBottom>
                                            {t('notas.createNewTarea', { notaType: notaType.toUpperCase() })}
                                          </Typography>
                                          <Grid container spacing={2} alignItems="center">
                                            <Grid item xs={12} sm={4}>
                                              <TextField
                                                fullWidth
                                                size="small"
                                                label={t('notas.name')}
                                                value={tareaForm[key]?.name || ''}
                                                onChange={(e) => handleTareaFormChange(key, 'name', e.target.value)}
                                              />
                                            </Grid>
                                            <Grid item xs={12} sm={3}>
                                              <TextField
                                                fullWidth
                                                size="small"
                                                type="number"
                                                label={t('notas.totalPoints')}
                                                value={tareaForm[key]?.total || ''}
                                                onChange={(e) => handleTareaFormChange(key, 'total', e.target.value)}
                                                inputProps={{ min: 0, step: 0.01 }}
                                              />
                                            </Grid>
                                            <Grid item xs={12} sm={5}>
                                              <Button
                                                variant="contained"
                                                size="small"
                                                startIcon={<AddIcon />}
                                                onClick={() => handleCreateTarea(curso.id, notaType)}
                                              >
                                                {t('notas.addTarea')}
                                              </Button>
                                            </Grid>
                                          </Grid>
                                        </Paper>
                                      </Box>
                                    </Collapse>
                                  </Paper>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              </>
            )}
          </CardContent>
        </Card>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NotasInput;
